import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shapes } from './Shapes';
import { Check, RefreshCw, Star } from 'lucide-react';
import confetti from 'canvas-confetti';
import Quiz from './Quiz';
import { useAuth } from '../../context/AuthContext';

import { sounds } from '../../utils/SoundManager';

const Section = ({ title, children, className }) => (
    <section className={`bg-white rounded-3xl p-6 shadow-xl border-4 border-brand-blue/10 ${className}`}>
        <h2 className="text-2xl md:text-3xl font-bold text-brand-purple mb-6 font-hand">{title}</h2>
        {children}
    </section>
);

const LineBox = ({ type, isOpen, onClick, isSelected }) => {
    return (
        <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
                sounds.playPop();
                if (onClick) onClick();
            }}
            className={`
        aspect-square rounded-2xl flex flex-col items-center justify-center p-4 transition-all
        ${isSelected ? 'bg-brand-yellow/20 ring-4 ring-brand-yellow' : 'bg-gray-50 hover:bg-gray-100'}
        border-2 border-gray-100
      `}
        >
            <div className="w-3/4 h-3/4 flex items-center justify-center">
                {type === 'rectas' && isOpen && <Shapes.StraightOpen />}
                {type === 'rectas' && !isOpen && (
                    <div className="text-center text-sm font-bold text-gray-400 leading-tight">
                        ¡No existen rectas cerradas! 🚫
                    </div>
                )}
                {type === 'curvas' && isOpen && <Shapes.CurvedOpen />}
                {type === 'curvas' && !isOpen && <Shapes.CurvedClosed />}
                {type === 'poligonales' && isOpen && <Shapes.PolygonalOpen />}
                {type === 'poligonales' && !isOpen && <Shapes.PolygonalClosed />}
            </div>
        </motion.button>
    );
};

// Activity Component
const TracingActivity = () => {
    const [completed, setCompleted] = useState([false, false, false, false]);
    const [activeTool, setActiveTool] = useState(null); // 'rectas', 'curva', 'poligonal'

    const shapes = [
        { id: 0, type: 'rectas', Shape: Shapes.StraightOpen },
        { id: 1, type: 'poligonales_cerradas', Shape: Shapes.PolygonalClosed },
        { id: 2, type: 'curvas', Shape: Shapes.CurvedOpen },
        { id: 3, type: 'poligonales_abiertas', Shape: Shapes.PolygonalOpen },
    ];

    const handleShapeClick = (index, type) => {
        if (activeTool === type) {
            const newCompleted = [...completed];
            newCompleted[index] = true;
            setCompleted(newCompleted);
            sounds.playCorrect();

            confetti({
                particleCount: 50,
                spread: 60,
                origin: { y: 0.7 }
            });
        } else {
            sounds.playIncorrect();
        }
    };

    const tools = [
        { id: 'rectas', color: 'bg-brand-yellow', label: 'Rectas', ring: 'ring-brand-yellow' },
        { id: 'curvas', color: 'bg-brand-blue', label: 'Curvas', ring: 'ring-brand-blue' },
        { id: 'poligonales_cerradas', color: 'bg-brand-green', label: 'Poligonales Cerradas', ring: 'ring-brand-green' },
        { id: 'poligonales_abiertas', color: 'bg-teal-500', label: 'Poligonales Abiertas', ring: 'ring-teal-500' },
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-center gap-4 flex-wrap">
                {tools.map((t) => (
                    <button
                        key={t.id}
                        onClick={() => {
                            setActiveTool(t.id);
                            sounds.playAction();
                        }}
                        className={`
              px-6 py-3 rounded-full font-bold text-white shadow-lg transition-all transform
              ${t.color}
              ${activeTool === t.id ? `scale-110 ring-4 ring-offset-2 ${t.ring}` : 'opacity-80 hover:opacity-100'}
            `}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 justify-items-center bg-gray-50 p-8 rounded-3xl">
                {shapes.map((s, i) => (
                    <motion.div
                        key={i}
                        whileHover={{ scale: 1.1 }}
                        onClick={() => handleShapeClick(i, s.type)}
                        className="w-32 h-32 relative cursor-pointer"
                    >
                        {/* The Base Shape */}
                        <div className={`transition-all duration-500 ${completed[i] ? 'opacity-100' : 'opacity-30 grayscale'}`}>
                            <s.Shape />
                        </div>

                        {/* Success Checkmark */}
                        {completed[i] && (
                            <motion.div
                                initial={{ scale: 0 }} animate={{ scale: 1 }}
                                className="absolute -top-2 -right-2 bg-green-500 text-white p-1 rounded-full shadow-lg"
                            >
                                <Check size={20} />
                            </motion.div>
                        )}
                    </motion.div>
                ))}
            </div>

            <div className="text-center text-gray-500 text-sm">
                💡 Selecciona un color arriba y toca la figura correcta para completarla.
            </div>
        </div>
    );
};

import { Link } from 'react-router-dom';

const LineClassification = () => {
    const { saveProgress } = useAuth();

    const quizQuestions = [
        {
            question: "¿Qué tipo de línea es esta nube?",
            visual: <Shapes.CurvedClosed />,
            options: ["Curva Abierta", "Curva Cerrada", "Recta"],
            correct: 1
        },
        {
            question: "Las líneas de tren son un ejemplo de...",
            visual: <Shapes.Parallel />,
            options: ["Rectas Secantes", "Rectas Paralelas", "Curvas"],
            correct: 1
        },
        {
            question: "¿Qué tipo de línea es este camino en zigzag?",
            visual: <div className="w-32 h-32 mx-auto"><Shapes.PolygonalOpen /></div>,
            options: ["Curva", "Poligonal Abierta", "Recta"],
            correct: 1
        },
        {
            question: "¡Verdadero o Falso! ¿Existen líneas RECTAS CERRADAS?",
            visual: <div className="text-6xl text-center">🚫</div>,
            options: ["¡Verdadero!", "¡Falso! (Sería un polígono)"],
            correct: 1
        },
        {
            question: "Estas dos líneas se cortan en un punto. Son...",
            visual: <div className="w-32 h-32 mx-auto"><Shapes.Secant /></div>,
            options: ["Paralelas", "Secantes", "Curvas"],
            correct: 1
        },
        {
            question: "Para dibujar este triángulo, ¿qué línea hemos usado?",
            visual: <div className="w-32 h-32 mx-auto"><Shapes.PolygonalClosed /></div>,
            options: ["Curva Cerrada", "Poligonal Abierta", "Poligonal Cerrada"],
            correct: 2
        },
        {
            question: "¿Cómo son las líneas de estas olas del mar?",
            visual: <div className="w-32 h-32 mx-auto"><Shapes.CurvedOpen /></div>,
            options: ["Rectas", "Curvas Abiertas", "Curvas Cerradas"],
            correct: 1
        }
    ];

    const handleQuizComplete = (score) => {
        saveProgress('unit1', score);
    };

    return (
        <div className="space-y-12 pb-20">
            {/* Header */}
            <header className="text-center space-y-2">
                <span className="bg-brand-yellow text-brand-orange px-4 py-1 rounded-full font-bold text-sm tracking-wide uppercase">
                    Unidad 1
                </span>
                <h1 className="text-4xl font-extrabold text-gray-800">Clasifico las líneas</h1>
                <p className="text-xl text-gray-500 mb-4">¡Las líneas nos ayudan a ver la forma de los objetos!</p>

                <Link to="/units/1/symmetry" className="inline-flex items-center gap-2 bg-brand-purple text-white px-6 py-3 rounded-full font-bold shadow-lg hover:scale-105 transition-transform animate-pulse">
                    ✨ Ir a Simetría 🦋
                </Link>
            </header>

            {/* Theory Grid */}
            <Section title="Tipos de Líneas">
                <div className="overflow-x-auto">
                    <div className="min-w-[500px] grid grid-cols-4 gap-4 text-center">
                        {/* Headers */}
                        <div className="p-4"></div>
                        <div className="p-4 font-bold text-brand-yellow text-xl">Rectas</div>
                        <div className="p-4 font-bold text-brand-blue text-xl">Curvas</div>
                        <div className="p-4 font-bold text-brand-green text-xl">Poligonales</div>

                        {/* Row: Abiertas */}
                        <div className="flex items-center justify-center font-bold text-gray-600">Abiertas</div>
                        <LineBox type="rectas" isOpen={true} />
                        <LineBox type="curvas" isOpen={true} />
                        <LineBox type="poligonales" isOpen={true} />

                        {/* Row: Cerradas */}
                        <div className="flex items-center justify-center font-bold text-gray-600">Cerradas</div>
                        <LineBox type="rectas" isOpen={false} />
                        <LineBox type="curvas" isOpen={false} />
                        <LineBox type="poligonales" isOpen={false} />
                    </div>
                </div>
            </Section>

            {/* Parallel vs Secant */}
            <div className="grid md:grid-cols-2 gap-6">
                <Section title="Rectas Paralelas">
                    <div className="h-40 flex items-center justify-center p-4">
                        <Shapes.Parallel />
                    </div>
                    <p className="text-center text-lg font-medium text-brand-orange">
                        ¡Nunca se cortan! <br /> <span className="text-sm text-gray-400">(Como las vías del tren 🚂)</span>
                    </p>
                </Section>
                <Section title="Rectas Secantes">
                    <div className="h-40 flex items-center justify-center p-4">
                        <Shapes.Secant />
                    </div>
                    <p className="text-center text-lg font-medium text-brand-orange">
                        Se cortan en un punto. <br /> <span className="text-sm text-gray-400">(Como una X ❌)</span>
                    </p>
                </Section>
            </div>

            {/* Activity */}
            <Section title="🎮 Misión: ¡Colorea las Líneas!" className="bg-blue-50 border-brand-blue">
                <TracingActivity />
            </Section>

            {/* Quiz Section */}
            <div className="py-8">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-extrabold text-brand-purple mb-2">🧠 ¡Comprueba lo que sabes!</h2>
                    <p className="text-gray-500">Responde a estas preguntas para ganar tu medalla.</p>
                </div>
                <div className="max-w-2xl mx-auto">
                    <Quiz questions={quizQuestions} onComplete={handleQuizComplete} />
                </div>
            </div>
        </div>
    );
};

export default LineClassification;
